<template>
    <div
        class="add-show-calculations"
    >
        <base-loader v-if="isLoading" />
        <template v-else>
            <div class="row has-border">
                <div class="text">
                    Additional cost:
                </div>
                <div class="calculation">
                    {{ usdFormatter(additionalAmount) }}
                    <span>/{{ interval }}</span>
                </div>
            </div>
            <div class="row">
                <div class="text">
                    Current {{ intervalFormatted }} payment ({{ showsCount }} {{ showsCountSuffix }}):
                </div>
                <div class="calculation is-small">
                    {{ usdFormatter(currentPaymentAmount) }}
                </div>
            </div>
            <div class="row">
                <div class="text">
                   New {{ intervalFormatted }} payment:
                </div>
                <div class="calculation">
                    {{ usdFormatter(incomingPaymentAmount) }}
                </div>
            </div>
        </template>
    </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import {
    toLower,
    isEmpty,
    reduce,
} from 'lodash';
import apiCall from '@/utils/apiCall';
import discountEndDate from '@/utils/user/discountEndDate';
import hasValidDiscount from '@/utils/user/hasValidDiscount';
import toasts from '@/mixins/toasts';
import usdFormatter from '@/utils/usdFormatter';

export default {
    mixins: [toasts],

    props: {
        billingPlan: {
            required: true,
            type: [Object, Boolean],
        },
        show: {
            type: Object,
            required: true,
        },
    },

    data: () => ({
        previewInvoiceData: null,
        previewInvoiceLoading: false,
    }),

    computed: {
        ...mapState([
            'billingPlans',
            'user',
            'userSubscription',
        ]),

        ...mapGetters({
            baseProduct: 'userSubscriptionPlan',
        }),

        /**
         *
         * @returns  {number}  User pro shows
         */
        showsCount() {
            return this.userSubscription?.items.data.reduce((sum, { quantity }) => sum + quantity, 0);
            // return this.baseProduct?.quantity;
        },

        /**
         *
         * @returns  {string}  Formated shows number suffix
         */
        showsCountSuffix() {
            return 1 === this.showsCount ? 'show' : 'shows';
        },

        /**
         * Returns base product interval.
         *
         * @returns {string} Base product interval.
         */
        interval() {
            return this.userSubscription?.interval;
        },

        /**
         * Returns base product formatted interval.
         *
         * @returns {string} Base product formatted interval.
         */
        intervalFormatted() {
            return {
                month: 'monthly',
                year: 'yearly',
            }[this.interval];
        },

        /**
         * @returns {object|null} Preview invoice.
         */
        previewInvoice() {
            if (this.previewInvoiceData || this.previewInvoiceLoading) {
                return this.previewInvoiceData;
            }

            this.loadPreviewInvoice();

            return null;
        },

        /**
         * Returns the additional payment amount.
         *
         * @returns {number} Additional payment amount.
         */
        additionalAmount() {
            return this.billingPlan.prices[this.interval].unitAmount;
        },

        /**
         * Returns current subscription total payment amount.
         *
         * @returns {number} User subscription total payment amount.
         */
        currentPaymentAmount() {
            return this.userSubscription.canceled_at
                ? 0
                : reduce(this.userSubscription.items.data, (amount, { price, quantity }) => {
                    return amount + (price.unit_amount * quantity);
                }, 0);
        },

        /**
         * Returns current subscription payment amount with new show amount.
         *
         * @returns {number} Incoming payment amount.
         */
        incomingPaymentAmount() {
            return this.previewInvoice?.total;
        },

        /**
         * Gets discounted plan price
         *
         * @returns  {number}  Discounted plan price in cents
         */
        discountedPlanAmount() {
            if (this.isValidDiscount) {
                return 'month' === this.interval ? 1200 : 800;
            }

            return false;
        },

        /**
         * Checks if user has valid discout
         *
         * @returns  {boolean}  If user has valid discount
         */
        isValidDiscount() {
            return this.user.legacy && hasValidDiscount(this.userSubscription?.discount?.end);
        },

        /**
         * Returns discount end date
         *0
         *
         * @returns  {string}  End date
         */
        discountDate() {
            return discountEndDate(this.userSubscription?.discount?.end);
        },

        /**
         * Determines if component is in loading state.
         *
         * @returns {boolean} Loading indicator.
         */
        isLoading() {
            return ! this.baseProduct ||
                ! this.userSubscription ||
                ! this.billingPlans ||
                ! this.billingPlan ||
                ! this.previewInvoice;
        },
    },

    methods: {
        toLower,
        isEmpty,
        usdFormatter,

        /**
         * sss
         */
        async loadPreviewInvoice() {
            if (! this.userSubscription) {
                return;
            }

            this.previewInvoiceLoading = true;

            const pricesToAdd = this.billingPlan.prices?.[this.interval]
                ? [this.billingPlan.prices[this.interval].id]
                : [];

            const pricesToRemove = this.show.plan?.prices?.[this.interval]
                ? [this.show.plan.prices[this.interval].id]
                : [];

            const { data, status } = await apiCall(
                this,
                'post',
                `/api/billing/subscriptions/${this.userSubscription.dbId}/invoices/preview`,
                {
                    prices_to_add: pricesToAdd,
                    prices_to_remove: pricesToRemove,
                }
            );

            if (200 !== status) {
                this.toastError({
                    message: {
                        title: data.message ||
                            'Something went wrong. PLease refresh the page and try again.',
                    },
                });

                return;
            }

            this.previewInvoiceData = data;
        },
    },
};
</script>
